<template>
    <div class="space-y-6">
        <!-- 后台刮削总开关 -->
        <div class="card bg-base-100 shadow-lg border border-base-200">
            <div class="card-body">
                <h2 class="card-title text-lg font-semibold mb-4 flex items-center">
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M8 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>{{ $tt('后台刮削设置') }}<div class="tooltip tooltip-bottom ml-2" :data-tip="$tt('立即执行一次')"
                        @click="handleBackendExe">
                        <button class="btn btn-circle btn-ghost btn-xs text-primary">
                            <svg-icon name="backend-exe" class="w-4 h-4 cursor-pointer fill-current" />
                        </button>
                    </div>
                </h2>

                <div class="bg-base-200/50 rounded-lg p-6 space-y-4">
                    <div class="form-control">
                        <label class="label cursor-pointer justify-start gap-4">
                            <span class="label-text font-medium">{{ $tt('开启后台刮削') }}</span>
                            <input type="checkbox" class="toggle toggle-primary toggle-lg"
                                v-model="formData.is_watch_file" @change="updateSys('is_watch_file')" />
                        </label>
                        <div class="mt-2 p-3 bg-info/10 rounded-lg border border-info/20"
                            v-if="celeryTaskLastTime && formData.is_watch_file">
                            <div class="text-sm text-base-content/80 flex items-center">
                                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                </svg>
                                {{ $tt('上次刮削时间: {time}', { time: celeryTaskLastTime }) }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 多套配置切换 -->
        <div class="card bg-base-100 shadow-lg border border-base-200">
            <div class="card-body">
                <div class="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <h2 class="card-title text-lg font-semibold">{{ $tt('刮削配置') }}</h2>
                    <div class="text-xs text-base-content/60">
                        {{ $tt('当前编辑:') }} {{ activeFormData ? (activeFormData.name || $tt('未命名配置')) : '-' }}
                    </div>
                </div>

                <div class="bg-base-200/50 rounded-xl border border-base-300 p-2">
                    <div class="flex items-stretch gap-2 overflow-x-auto">
                        <div
                            v-for="config in backgroundScrapeConfigs"
                            :key="config.id"
                            class="group flex-none min-w-[170px] max-w-[230px] rounded-lg border px-3 py-2 cursor-pointer transition-all"
                            :class="config.id === activeConfigId
                                ? 'bg-base-100 border-primary shadow-sm ring-1 ring-primary/30'
                                : 'bg-base-100/70 border-base-300 hover:border-primary/40 hover:bg-base-100'"
                            @click="changeActiveConfig(config.id)"
                            @dblclick.stop="startRenameConfig(config)">
                            <div class="flex items-center justify-between gap-2">
                                <div class="flex items-center gap-2 min-w-0">
                                    <span
                                        class="inline-block w-2 h-2 rounded-full flex-none"
                                        :class="config.enabled === false ? 'bg-base-content/30' : 'bg-success'">
                                    </span>
                                    <input
                                        v-if="editingConfigId === config.id"
                                        :ref="`renameInput-${config.id}`"
                                        v-model="editingConfigName"
                                        class="input input-xs input-bordered w-28 bg-base-100 text-base-content placeholder:text-base-content/50"
                                        @click.stop
                                        @keydown="handleRenameKeydown"
                                        @blur="finishRenameConfig" />
                                    <div v-else class="font-medium truncate">{{ config.name || $tt('未命名配置') }}</div>
                                </div>
                                <button
                                    v-if="backgroundScrapeConfigs.length > 1"
                                    class="btn btn-ghost btn-xs w-4 h-4 min-h-0 min-w-0 p-0 opacity-0 group-hover:opacity-100"
                                    :title="$tt('删除配置')"
                                    @click.stop="removeActiveConfigTab(config.id)">
                                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                                    </svg>
                                </button>
                            </div>
                            <div class="mt-1 text-xs text-base-content/60 truncate">
                                {{ config.watch_subdir }}
                            </div>
                        </div>

                        <button
                            class="flex-none min-w-[52px] rounded-lg border border-dashed border-base-400 hover:border-primary hover:text-primary transition-colors"
                            :title="$tt('新增配置')"
                            @click="addConfigTab">
                            +
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- 监控目录设置 -->
        <div class="card bg-base-100 shadow-lg border border-base-200">
            <div class="card-body">
                <h2 class="card-title text-lg font-semibold mb-4 flex items-center">
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"></path>
                    </svg>{{ $tt('监控目录配置') }}
                    <div class="tooltip tooltip-bottom ml-2" :data-tip="$tt('标记当前目录已刮削完成')">
                        <button class="btn btn-circle btn-ghost btn-xs text-success" @click.stop="handleMarkBackgroundDone">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                            </svg>
                        </button>
                    </div>
                    <div class="tooltip tooltip-bottom ml-1" :data-tip="$tt('清空后台刮削记录')">
                        <button class="btn btn-circle btn-ghost btn-xs text-warning" @click.stop="handleClearBackgroundState">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3m-7 0h8"></path>
                            </svg>
                        </button>
                    </div><div class="tooltip tooltip-bottom ml-2" :data-tip="$tt('配置帮助')">
                        <button class="btn btn-circle btn-ghost btn-xs text-info" @click.stop="openConfigHelpDrawer">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z">
                                </path>
                            </svg>
                        </button>
                    </div>
                </h2>

                <div class="space-y-4">
                    <div class="form-control">
                        <div v-if="activeFormData" class="space-y-2 mb-3">
                            <label
                                class="label cursor-pointer justify-start gap-4 p-2 rounded-lg border border-base-200">
                                <span class="label-text font-medium">{{ $tt('启用此配置') }}</span>
                                <input v-model="activeFormData.enabled" type="checkbox" class="toggle toggle-primary"
                                    @change="updateSys('enabled')" />
                            </label>
                        </div>
                        <label class="label">
                            <span class="label-text font-medium">{{ $tt('监控目录（容器内地址）') }}</span>
                        </label>
                        <div v-if="activeFormData" class="space-y-2 mb-3">
                            <input list="watch-subdir-options" :value="activeFormData.watch_subdir" type="text"
                                class="input input-bordered w-full"
                                :placeholder="$tt('请输入监控目录，例如：/app/download/qbittorrent')"
                                @change="handleWatchSubdirChange($event.target.value)" />
                            <datalist id="watch-subdir-options">
                                <option v-for="dir in availableWatchDirs" :key="dir" :value="`${watchRoot}/${dir}`">
                                </option>
                            </datalist>
                        </div>
                        <label class="label">
                            <span class="label-text-alt text-base-content/70">
                                {{ $tt('后台刮削的监控目录，会对该目录下新增的音乐文件进行以下一系列操作') }}
                                <button class="btn btn-xs btn-outline btn-primary ml-2"
                                    @click="openScrapeScheduleConfig">
                                    <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                    </svg>{{ $tt('配置扫描周期') }}</button>
                            </span>
                        </label>
                    </div>
                </div>
            </div>
        </div>
        <!-- 音乐预处理 -->
        <li class="font-medium text-xl ml-2">Step 1</li>
        <div class="card bg-base-100 shadow-lg border border-base-200">
            <div class="collapse collapse-arrow">
                <input type="checkbox" v-model="preDataCollapse" @click.stop />
                <div class="collapse-title card-title text-lg font-semibold flex items-center"
                    @click.stop="toggleCollapse('preDataCollapse')">
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z">
                        </path>
                    </svg>{{ $tt('音乐预处理') }}
                </div>
                <div class="collapse-content card-body pt-0">
                    <div class="space-y-3">
                        <div class="form-control">
                            <label
                                class="label cursor-pointer justify-start gap-4 p-4 rounded-lg border border-base-200 hover:bg-base-50">
                                <input type="checkbox" class="checkbox checkbox-primary"
                                    v-model="activeFormData.is_copy_file" @change="updateSys('is_copy_file')" />
                                <div class="flex-1">
                                    <div class="font-medium text-base-content flex items-center">{{ $tt('复制文件') }}<div
                                            class="tooltip tooltip-bottom ml-2" :data-tip="$tt('配置复制文件参数')"
                                            @click.stop="openCopyFileConfig">
                                            <button class="btn btn-circle btn-ghost btn-xs text-primary">
                                                <svg class="w-4 h-4" fill="none" stroke="currentColor"
                                                    viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2"
                                                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z">
                                                    </path>
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                    <div class="text-sm text-base-content/70 mt-1">
                                        {{ $tt('复制音乐文件到指定目录，可选择复制文件本体或生成strm文件') }}
                                    </div>
                                </div>
                            </label>
                        </div>
                        <div class="form-control">
                            <label
                                class="label cursor-pointer justify-start gap-4 p-4 rounded-lg border border-base-200 hover:bg-base-50">
                                <input type="checkbox" class="checkbox checkbox-primary"
                                    v-model="activeFormData.is_voiceprint" @change="updateSys('is_voiceprint')" />
                                <div class="flex-1">
                                    <div class="font-medium text-base-content">{{ $tt('声纹识别') }}</div>
                                    <div class="text-sm text-base-content/70 mt-1">
                                        {{ $tt('当音乐文件缺少艺术家或专辑元数据时, 使用声纹识别进行预匹配') }}
                                    </div>
                                </div>
                            </label>
                        </div>

                        <div class="form-control">
                            <label
                                class="label cursor-pointer justify-start gap-4 p-4 rounded-lg border border-base-200 hover:bg-base-50">
                                <input type="checkbox" class="checkbox checkbox-primary"
                                    v-model="activeFormData.is_fix_encoding" @change="updateSys('is_fix_encoding')" />
                                <div class="flex-1">
                                    <div class="font-medium text-base-content">{{ $tt('乱码修复') }}</div>
                                    <div class="text-sm text-base-content/70 mt-1">
                                        {{ $tt('对标题、艺术家、专辑名进行乱码修复') }}
                                    </div>
                                </div>
                            </label>
                        </div>

                        <div class="form-control">
                            <label
                                class="label cursor-pointer justify-start gap-4 p-4 rounded-lg border border-base-200 hover:bg-base-50">
                                <input type="checkbox" class="checkbox checkbox-primary"
                                    v-model="activeFormData.is_tran_sim" @change="updateSys('is_tran_sim')" />
                                <div class="flex-1">
                                    <div class="font-medium text-base-content flex items-center">{{ $tt('简繁转换') }}<div
                                            class="tooltip tooltip-bottom ml-2" :data-tip="$tt('配置简繁转换参数')"
                                            @click.stop="openTranConfig">
                                            <button class="btn btn-circle btn-ghost btn-xs text-primary">
                                                <svg class="w-4 h-4" fill="none" stroke="currentColor"
                                                    viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2"
                                                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z">
                                                    </path>
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                    <div class="text-sm text-base-content/70 mt-1">
                                        {{ $tt('自动识别简体中文和繁体中文，支持双向转换。适用于音乐标签中的艺术家、专辑、歌曲名等字段的字体转换。') }}
                                    </div>
                                </div>
                            </label>
                        </div>

                        <div class="form-control">
                            <label
                                class="label cursor-pointer justify-start gap-4 p-4 rounded-lg border border-base-200 hover:bg-base-50">
                                <input type="checkbox" class="checkbox checkbox-primary"
                                    v-model="activeFormData.is_split_cue" @change="updateSys('is_split_cue')" />
                                <div class="flex-1">
                                    <div class="font-medium text-base-content flex items-center">{{ $tt('切割CUE音轨') }}<div
                                            class="tooltip tooltip-bottom ml-2" :data-tip="$tt('配置 CUE 切割参数')"
                                            @click.stop="openSplitCueConfig">
                                            <button class="btn btn-circle btn-ghost btn-xs text-primary">
                                                <svg class="w-4 h-4" fill="none" stroke="currentColor"
                                                    viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2"
                                                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z">
                                                    </path>
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                    <div class="text-sm text-base-content/70 mt-1">
                                        {{ $tt('检测复制后的音频旁是否存在同名 CUE 文件，存在时先切割整轨再继续刮削分轨。') }}
                                    </div>
                                </div>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- 音乐自动刮削 -->
        <li class="font-medium text-xl ml-2">Step 2</li>
        <div class="card bg-base-100 shadow-lg border border-base-200">
            <div class="collapse collapse-arrow">
                <input type="checkbox" v-model="autoModCollapse" @click.stop />
                <div class="collapse-title card-title text-lg font-semibold flex items-center"
                    @click.stop="toggleCollapse('autoModCollapse')">
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z">
                        </path>
                    </svg>{{ $tt('音乐自动刮削') }}
                </div>
                <div class="collapse-content card-body pt-0">
                    <div class="space-y-3">
                        <div class="form-control">
                            <label
                                class="label cursor-pointer justify-start gap-4 p-4 rounded-lg border border-base-200 hover:bg-base-50">
                                <input type="checkbox" class="checkbox checkbox-secondary"
                                    v-model="activeFormData.auto_mod" @change="updateSys('auto_mod')" />
                                <div class="flex-1">
                                    <div class="font-medium text-base-content flex items-center">{{ $tt('自动刮削') }}<div
                                            class="tooltip tooltip-bottom ml-2" :data-tip="$tt('配置自动刮削参数')"
                                            @click.stop="openAutoModConfig">
                                            <button class="btn btn-circle btn-ghost btn-xs text-primary">
                                                <svg class="w-4 h-4" fill="none" stroke="currentColor"
                                                    viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2"
                                                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z">
                                                    </path>
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                    <div class="text-sm text-base-content/70 mt-1">
                                        {{ $tt('可自动从音乐流媒体平台搜索元数据实行刮削操作') }}
                                    </div>
                                </div>
                            </label>
                        </div>

                        <div class="form-control">
                            <label
                                class="label cursor-pointer justify-start gap-4 p-4 rounded-lg border border-base-200 hover:bg-base-50">
                                <input type="checkbox" class="checkbox checkbox-secondary"
                                    v-model="activeFormData.manual_mod" @change="updateSys('manual_mod')" />
                                <div class="flex-1">
                                    <div class="font-medium text-base-content flex items-center">{{ $tt('手动刮削') }}<div
                                            class="tooltip tooltip-bottom ml-2" :data-tip="$tt('配置手动刮削参数')"
                                            @click.stop="openManualModConfig">
                                            <button class="btn btn-circle btn-ghost btn-xs text-primary">
                                                <svg class="w-4 h-4" fill="none" stroke="currentColor"
                                                    viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2"
                                                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z">
                                                    </path>
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                    <div class="text-sm text-base-content/70 mt-1">
                                        {{ $tt('可自定义修改音乐元数据中的内容，例如：重命名文件、导出歌词文件、导出封面图片等') }}
                                    </div>
                                </div>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- 音乐文件整理 -->
        <li class="font-medium text-xl ml-2">Step 3</li>
        <div class="card bg-base-100 shadow-lg border border-base-200">
            <div class="collapse collapse-arrow">
                <input type="checkbox" v-model="tidyCollapse" @click.stop />
                <div class="collapse-title card-title text-lg font-semibold flex items-center"
                    @click.stop="toggleCollapse('tidyCollapse')">
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M7 16V4m0 0L3 8l4 4m0-8l4 4-4 4m6 0v12m0 0l4-4-4-4m0 8l-4-4 4-4"></path>
                    </svg>{{ $tt('音乐文件整理') }}
                </div>
                <div class="collapse-content card-body pt-0">
                    <div class="space-y-3">
                        <div class="form-control">
                            <label
                                class="label cursor-pointer justify-start gap-4 p-4 rounded-lg border border-base-200 hover:bg-base-50">
                                <input type="radio" name="radio-1" class="radio radio-accent" value=''
                                    v-model="activeFormData.tidy_music" @change="updateSys('tidy_music')" />
                                <div class="flex-1">
                                    <div class="font-medium text-base-content flex items-center">{{ $tt('不整理') }}<div
                                            class="tooltip tooltip-bottom ml-2" :data-tip="$tt('保持原目录,不进行任何整理或转移操作')">
                                            <svg class="w-4 h-4 text-info" fill="none" stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z">
                                                </path>
                                            </svg>
                                        </div>
                                    </div>
                                    <div class="text-sm text-base-content/70 mt-1">
                                        {{ $tt('保持原目录,不进行任何整理或转移操作') }}
                                    </div>
                                </div>
                            </label>
                        </div>

                        <div class="form-control">
                            <label
                                class="label cursor-pointer justify-start gap-4 p-4 rounded-lg border border-base-200 hover:bg-base-50">
                                <input type="radio" name="radio-1" class="radio radio-accent" value='tidy'
                                    v-model="activeFormData.tidy_music" @change="updateSys('tidy_music')" />
                                <div class="flex-1">
                                    <div class="font-medium text-base-content flex items-center">{{ $tt('整理文件') }}<div
                                            class="tooltip tooltip-bottom ml-2" :data-tip="$tt('配置整理文件参数')"
                                            @click.stop="openTidyMusicConfig">
                                            <button class="btn btn-circle btn-ghost btn-xs text-primary">
                                                <svg class="w-4 h-4" fill="none" stroke="currentColor"
                                                    viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2"
                                                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z">
                                                    </path>
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                    <div class="text-sm text-base-content/70 mt-1">
                                        {{ $tt('将文件以规定的格式整理到统一目录下') }}
                                    </div>
                                </div>
                            </label>
                        </div>

                    </div>
                </div>
            </div>
        </div>

        <!-- 自动刮削抽屉 -->
        <div class="drawer drawer-end">
            <input id="auto-mod" type="checkbox" class="drawer-toggle" v-model="isOpenAutoMod" />
            <div class="drawer-content">
            </div>
            <div class="drawer-side" style="z-index: 1000;">
                <label for="auto-mod" aria-label="close sidebar" class="drawer-overlay"
                    @click.stop="isOpenAutoMod = false"></label>
                <div class="bg-base-100 text-base-content min-h-full w-80 md:w-96" v-if="isOpenAutoMod" @click.stop>
                    <div class="sticky top-0 bg-base-100 z-10 p-4 border-b border-base-200">
                        <div class="flex items-center justify-between">
                            <h3 class="text-lg font-semibold flex items-center">
                                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z">
                                    </path>
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                </svg>{{ $tt('自动刮削配置') }}
                            </h3>
                            <button class="btn btn-circle btn-ghost btn-sm" @click="isOpenAutoMod = false">
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M6 18L18 6M6 6l12 12"></path>
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div class="p-4 space-y-6">
                        <div class="bg-info/10 p-4 rounded-lg border border-info/20">
                            <div class="text-sm text-base-content/80 space-y-1">
                                <p>{{ $tt('根据元数据中的标题、艺术家、专辑和文件名、文件完整路径进行匹配。') }}</p>
                                <p>{{ $tt('标题、艺术家、专辑每一项匹配等级指标：完全一致，包含，不匹配') }}</p>
                            </div>
                        </div>

                        <div class="form-control">
                            <label class="label">
                                <span class="label-text font-medium">{{ $tt('匹配模式') }}</span>
                            </label>
                            <div class="space-y-2">
                                <label
                                    class="label cursor-pointer justify-start gap-3 p-3 rounded-lg border border-base-200 hover:bg-base-50">
                                    <input type="radio" name="radio-auto-mode" class="radio radio-primary"
                                        value="simple" v-model="activeFormData.silent_select_mode"
                                        @change="updateSys('silent_select_mode')" />
                                    <div class="flex-1">
                                        <div class="font-medium">{{ $tt('宽松模式') }}</div>
                                        <div class="text-xs text-base-content/70">{{ $tt('满足标题包含即可，但也优先选完全一致') }}</div>
                                    </div>
                                </label>
                                <label
                                    class="label cursor-pointer justify-start gap-3 p-3 rounded-lg border border-base-200 hover:bg-base-50">
                                    <input type="radio" name="radio-auto-mode" class="radio radio-primary"
                                        value="normal" v-model="activeFormData.silent_select_mode"
                                        @change="updateSys('silent_select_mode')" />
                                    <div class="flex-1">
                                        <div class="font-medium">{{ $tt('标准模式') }}</div>
                                        <div class="text-xs text-base-content/70">{{ $tt('三个指标必须为包含或完全一致，但也优先选完全一致') }}
                                        </div>
                                    </div>
                                </label>
                                <label
                                    class="label cursor-pointer justify-start gap-3 p-3 rounded-lg border border-base-200 hover:bg-base-50">
                                    <input type="radio" name="radio-auto-mode" class="radio radio-primary" value="hard"
                                        v-model="activeFormData.silent_select_mode"
                                        @change="updateSys('silent_select_mode')" />
                                    <div class="flex-1">
                                        <div class="font-medium">{{ $tt('严格模式') }}</div>
                                        <div class="text-xs text-base-content/70">{{ $tt('三个指标必须都为完全一致') }}</div>
                                    </div>
                                </label>
                            </div>
                        </div>

                        <div class="form-control">
                            <label class="label">
                                <span class="label-text font-medium">{{ $tt('标签源选择') }}</span>
                            </label>
                            <multi-select :options="resourceListBatch" v-model="activeFormData.silence_source_list"
                                @change="updateSys('silence_source_list')">
                            </multi-select>
                        </div>

                        <div class="form-control">
                            <label class="label">
                                <span class="label-text font-medium">{{ $tt('修改范围') }}</span>
                            </label>
                            <multi-select :options="modifyListBatch" v-model="activeFormData.silence_modify_list"
                                @change="updateSys('silence_modify_list')">
                            </multi-select>
                        </div>

                        <div class="form-control">
                            <label class="label cursor-pointer justify-start gap-4">
                                <span class="label-text font-medium">{{ $tt('跳过匹配') }}</span>
                                <input type="checkbox" class="toggle toggle-primary"
                                    @change="updateSys('silence_is_skip_tag')"
                                    v-model="activeFormData.silence_is_skip_tag" />
                            </label>
                            <div class="mt-2 p-3 bg-base-200/50 rounded-lg">
                                <div class="text-sm text-base-content/70">
                                    {{ $tt('如修改范围中元数据都存在，则跳过匹配，原有音乐标签将被保留，不会被修改或覆盖') }}
                                </div>
                            </div>
                        </div>

                        <div class="form-control">
                            <label class="label cursor-pointer justify-start gap-4">
                                <span class="label-text font-medium">{{ $tt('同目录解释为同个专辑') }}</span>
                                <input type="checkbox" class="toggle toggle-primary"
                                    @change="updateSys('silence_is_folder_album')"
                                    v-model="activeFormData.silence_is_folder_album" />
                            </label>
                            <div class="mt-2 p-3 bg-base-200/50 rounded-lg">
                                <div class="text-sm text-base-content/70">
                                    {{ $tt('当元数据中不存在专辑信息，用于补充专辑信息，提高匹配度') }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 手动刮削抽屉 -->
        <div class="drawer drawer-end">
            <input id="manual-mod" type="checkbox" class="drawer-toggle" v-model="isOpenManualMod" />
            <div class="drawer-content">
            </div>
            <div class="drawer-side" style="z-index: 1000;">
                <label for="manual-mod" aria-label="close sidebar" class="drawer-overlay"
                    @click.stop="isOpenManualMod = false"></label>
                <div class="bg-base-100 text-base-content min-h-full w-80 md:w-96" v-if="isOpenManualMod" @click.stop>
                    <div class="sticky top-0 bg-base-100 z-10 p-4 border-b border-base-200">
                        <div class="flex items-center justify-between">
                            <h3 class="text-lg font-semibold flex items-center">
                                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z">
                                    </path>
                                </svg>{{ $tt('手动刮削配置') }}
                            </h3>
                            <button class="btn btn-primary btn-sm gap-1" @click="handleSaveManualMusic">
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M5 13l4 4L19 7"></path>
                                </svg>{{ $tt('保存并关闭') }}</button>
                        </div>
                    </div>

                    <div class="p-4">
                        <music-meta-data :music-info="activeFormData.silence_music_info" :reload-img="reloadImg"
                            :drawer-mode="'drawer'" :mode="'form'">
                        </music-meta-data>
                    </div>
                </div>
            </div>
        </div>

        <!-- 文件整理抽屉 -->
        <div class="drawer drawer-end">
            <input id="tidy-music" type="checkbox" class="drawer-toggle" v-model="isOpenTidyMusic" />
            <div class="drawer-content">
            </div>
            <div class="drawer-side" style="z-index: 1000;">
                <label for="tidy-music" aria-label="close sidebar" class="drawer-overlay"
                    @click.stop="isOpenTidyMusic = false"></label>
                <div class="bg-base-100 text-base-content min-h-full w-80 md:w-96" v-if="isOpenTidyMusic" @click.stop>
                    <div class="sticky top-0 bg-base-100 z-10 p-4 border-b border-base-200">
                        <div class="flex items-center justify-between">
                            <h3 class="text-lg font-semibold flex items-center">
                                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"></path>
                                </svg>{{ $tt('文件整理配置') }}
                            </h3>
                            <button class="btn btn-circle btn-ghost btn-sm" @click="isOpenTidyMusic = false">
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M6 18L18 6M6 6l12 12"></path>
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div class="p-4 space-y-6">
                        <div class="bg-info/10 p-4 rounded-lg border border-info/20">
                            <div class="text-sm text-base-content/80">
                                {{ $tt('整理文件夹，按一级目录，二级目录选定的信息分类') }}
                            </div>
                        </div>

                        <div class="form-control">
                            <label class="label">
                                <span class="label-text font-medium">{{ $tt('整理后的根目录') }}</span>
                            </label>
                            <input type="text" :placeholder="$tt('请输入根目录路径')"
                                class="input input-bordered focus:input-accent w-full"
                                v-model="activeFormData.root_path" @change="updateSys('root_path')" />
                        </div>

                        <div class="form-control">
                            <label class="label">
                                <span class="label-text font-medium">{{ $tt('目录层级设置') }}</span>
                            </label>
                            <div class="space-y-3">
                                <div v-for="(input, index) in activeFormData.dir_list" :key="index"
                                    class="p-3 border border-base-200 rounded-lg">
                                    <div class="flex items-center justify-between mb-2">
                                        <span class="text-sm font-medium">{{ $tt('第 {level} 层目录', { level: index + 1 })
                                            }}</span>
                                        <button class="btn btn-circle btn-ghost btn-xs text-error"
                                            @click="removeInput(index)">
                                            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M6 18L18 6M6 6l12 12"></path>
                                            </svg>
                                        </button>
                                    </div>
                                    <single-select v-model="input.value" :options="tidyList" :placeholder="$tt('请选择')"
                                        @change="updateSys('dir_list')">
                                    </single-select>
                                    <div v-if="input.value === 'custom'" class="mt-2">
                                        <label class="label">
                                            <span class="label-text text-sm">{{ $tt('自定义变量') }}</span>
                                        </label>
                                        <input type="text" :placeholder="$tt('请输入自定义变量，例如：${artist}-${album}')"
                                            class="input input-sm input-bordered w-full" v-model="input.value2"
                                            @change="updateSys('dir_list')" />
                                    </div>
                                </div>
                                <button class="btn btn-outline btn-sm gap-2 w-full" @click="addInput">
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                                    </svg>{{ $tt('添加目录层级') }}</button>
                            </div>
                        </div>

                        <div class="form-control">
                            <label class="label cursor-pointer justify-start gap-4">
                                <span class="label-text font-medium">{{ $tt('启用音乐洗版') }}</span>
                                <input type="checkbox" class="toggle toggle-warning"
                                    v-model="activeFormData.is_clean_music" @change="updateSys('is_clean_music')" />
                            </label>
                            <div class="mt-2 p-3 bg-warning/10 rounded-lg border border-warning/20">
                                <div class="text-sm text-base-content/80">
                                    {{ $tt('音乐洗版就是按以下规则换更好的版本') }}
                                </div>
                            </div>
                        </div>

                        <div v-if="activeFormData.is_clean_music"
                            class="space-y-4 p-4 bg-base-200/50 rounded-lg border border-base-200">
                            <h4 class="font-medium text-base-content">{{ $tt('洗版规则配置') }}</h4>

                            <div class="form-control">
                                <label class="label">
                                    <span class="label-text font-medium">{{ $tt('格式优先级') }}</span>
                                </label>
                                <multi-select :options="formatList" v-model="activeFormData.clean_format_priority"
                                    @change="updateSys('clean_format_priority')">
                                </multi-select>
                            </div>

                            <div class="grid grid-cols-1 gap-4">
                                <div class="form-control">
                                    <label class="label">
                                        <span class="label-text font-medium">{{ $tt('文件大小规则') }}</span>
                                    </label>
                                    <single-select v-model="activeFormData.clean_size_rule" :options="sizeRuleList"
                                        :placeholder="$tt('请选择')" @change="updateSys('clean_size_rule')">
                                    </single-select>
                                </div>

                                <div class="form-control">
                                    <label class="label">
                                        <span class="label-text font-medium">{{ $tt('比特率规则') }}</span>
                                    </label>
                                    <single-select v-model="activeFormData.clean_bitrate_rule" :options="bitRuleList"
                                        :placeholder="$tt('请选择')" @change="updateSys('clean_bitrate_rule')">
                                    </single-select>
                                </div>

                                <div class="form-control">
                                    <label class="label">
                                        <span class="label-text font-medium">{{ $tt('采样率/位深规则') }}</span>
                                    </label>
                                    <single-select v-model="activeFormData.clean_sample_rule" :options="sampleRuleList"
                                        :placeholder="$tt('请选择')" @change="updateSys('clean_sample_rule')">
                                    </single-select>
                                </div>

                                <div class="form-control">
                                    <label class="label">
                                        <span class="label-text font-medium">{{ $tt('文件名规则') }}</span>
                                    </label>
                                    <single-select v-model="activeFormData.clean_name_rule" :options="nameRuleList"
                                        :placeholder="$tt('请选择')" @change="updateSys('clean_name_rule')">
                                    </single-select>
                                </div>

                                <div class="form-control">
                                    <label class="label">
                                        <span class="label-text font-medium">{{ $tt('删除策略') }}</span>
                                    </label>
                                    <single-select v-model="activeFormData.clean_delete_rule" :options="deleteRuleList"
                                        :placeholder="$tt('请选择')" @change="updateSys('clean_delete_rule')">
                                    </single-select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 复制文件抽屉 -->
        <div class="drawer drawer-end">
            <input id="copy-file-config" type="checkbox" class="drawer-toggle" v-model="isOpenCopyFile" />
            <div class="drawer-content">
            </div>
            <div class="drawer-side" style="z-index: 1000;">
                <label for="copy-file-config" aria-label="close sidebar" class="drawer-overlay"
                    @click.stop="isOpenCopyFile = false"></label>
                <div class="bg-base-100 text-base-content min-h-full w-80 md:w-96" v-if="isOpenCopyFile" @click.stop>
                    <div class="sticky top-0 bg-base-100 z-10 p-4 border-b border-base-200">
                        <div class="flex items-center justify-between">
                            <h3 class="text-lg font-semibold flex items-center">
                                <svg class="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path
                                        d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                                </svg>{{ $tt('复制文件配置') }}
                            </h3>
                            <button class="btn btn-circle btn-ghost btn-sm" @click="isOpenCopyFile = false">
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M6 18L18 6M6 6l12 12"></path>
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div class="p-4 space-y-6">
                        <div class="bg-info/10 p-4 rounded-lg border border-info/20">
                            <div class="text-sm text-base-content/80">
                                {{ $tt('复制音乐文件到指定目录，可选择复制文件本体或生成strm流媒体文件') }}
                            </div>
                        </div>

                        <div class="form-control">
                            <label class="label">
                                <span class="label-text font-medium">{{ $tt('目标目录') }}</span>
                            </label>
                            <input type="text" :placeholder="$tt('请输入目标目录路径（容器内地址）')"
                                class="input input-bordered focus:input-accent w-full"
                                v-model="activeFormData.copy_target_path" @change="updateSys('copy_target_path')" />
                            <label class="label">
                                <span class="label-text-alt text-base-content/60">{{ $tt('例如: /app/media/') }}</span>
                            </label>
                        </div>

                        <div class="form-control">
                            <label class="label">
                                <span class="label-text font-medium">{{ $tt('复制模式') }}</span>
                            </label>
                            <div class="space-y-2">
                                <label
                                    class="label cursor-pointer justify-start gap-3 p-3 rounded-lg border border-base-200 hover:bg-base-50">
                                    <input type="radio" name="radio-copy-mode" class="radio radio-success" value="copy"
                                        v-model="activeFormData.copy_mode" @change="updateSys('copy_mode')" />
                                    <div class="flex-1">
                                        <div class="font-medium">{{ $tt('复制文件本体') }}</div>
                                        <div class="text-xs text-base-content/70">{{ $tt('完整复制音乐文件到目标目录') }}</div>
                                    </div>
                                </label>
                                <label
                                    class="label cursor-pointer justify-start gap-3 p-3 rounded-lg border border-base-200 hover:bg-base-50">
                                    <input type="radio" name="radio-copy-mode" class="radio radio-success" value="strm"
                                        v-model="activeFormData.copy_mode" @change="updateSys('copy_mode')" />
                                    <div class="flex-1">
                                        <div class="font-medium">{{ $tt('生成strm文件') }}</div>
                                        <div class="text-xs text-base-content/70">{{ $tt('创建strm流媒体文件指向原始文件') }}</div>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 简繁转换抽屉 -->
        <div class="drawer drawer-end">
            <input id="tran-config" type="checkbox" class="drawer-toggle" v-model="isOpenTranConfig" />
            <div class="drawer-content">
            </div>
            <div class="drawer-side" style="z-index: 1000;">
                <label for="tran-config" aria-label="close sidebar" class="drawer-overlay"
                    @click.stop="isOpenTranConfig = false"></label>
                <div class="bg-base-100 text-base-content min-h-full w-80 md:w-96" v-if="isOpenTranConfig" @click.stop>
                    <div class="sticky top-0 bg-base-100 z-10 p-4 border-b border-base-200">
                        <div class="flex items-center justify-between">
                            <h3 class="text-lg font-semibold flex items-center">
                                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M3 5h12M9 3v2m1.048 9A18.022 18.022 0 016.412 9m6.088 9h7m-7 0a20.023 20.023 0 003.81-7.5m3.19 7.5L21 21">
                                    </path>
                                </svg>{{ $tt('简繁转换配置') }}
                            </h3>
                            <button class="btn btn-circle btn-ghost btn-sm" @click="isOpenTranConfig = false">
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M6 18L18 6M6 6l12 12"></path>
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div class="p-4 space-y-6">
                        <div class="bg-info/10 p-4 rounded-lg border border-info/20">
                            <div class="text-sm text-base-content/80">
                                {{ $tt('自动识别简体中文和繁体中文，支持双向转换。适用于音乐标签中的艺术家、专辑、歌曲名等字段的字体转换。') }}
                            </div>
                        </div>

                        <div class="form-control">
                            <label class="label">
                                <span class="label-text font-medium">{{ $tt('转换方向') }}</span>
                            </label>
                            <div class="space-y-2">
                                <label
                                    v-for="option in tranConvertModeOptions"
                                    :key="option.id"
                                    class="label cursor-pointer justify-start gap-3 p-3 rounded-lg border border-base-200 hover:bg-base-50">
                                    <input type="radio" name="radio-tran-mode" class="radio radio-primary"
                                        :value="option.id" v-model="activeFormData.tran_convert_mode"
                                        @change="updateSys('tran_convert_mode')" />
                                    <div class="flex-1">
                                        <div class="font-medium">{{ option.name }}</div>
                                        <div class="text-xs text-base-content/70">{{ option.description }}</div>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- CUE切割抽屉 -->
        <div class="drawer drawer-end">
            <input id="split-cue-config" type="checkbox" class="drawer-toggle" v-model="isOpenSplitCueConfig" />
            <div class="drawer-content">
            </div>
            <div class="drawer-side" style="z-index: 1000;">
                <label for="split-cue-config" aria-label="close sidebar" class="drawer-overlay"
                    @click.stop="isOpenSplitCueConfig = false"></label>
                <div class="bg-base-100 text-base-content min-h-full w-80 md:w-96" v-if="isOpenSplitCueConfig" @click.stop>
                    <div class="sticky top-0 bg-base-100 z-10 p-4 border-b border-base-200">
                        <div class="flex items-center justify-between">
                            <h3 class="text-lg font-semibold flex items-center">
                                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M7 4v16M17 4v16M3 8h4m10 0h4M3 16h4m10 0h4">
                                    </path>
                                </svg>{{ $tt('CUE 切割配置') }}
                            </h3>
                            <button class="btn btn-circle btn-ghost btn-sm" @click="isOpenSplitCueConfig = false">
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M6 18L18 6M6 6l12 12"></path>
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div class="p-4 space-y-6">
                        <div class="bg-info/10 p-4 rounded-lg border border-info/20">
                            <div class="text-sm text-base-content/80 space-y-2">
                                <p>{{ $tt('检测复制后的音频旁是否存在同名 CUE 文件，存在时先切割整轨再继续刮削分轨。') }}</p>
                                <p class="text-warning">{{ $tt('启用 CUE 切割时，复制模式必须为复制文件本体。') }}</p>
                            </div>
                        </div>

                        <div class="form-control">
                            <label class="label">
                                <span class="label-text font-medium">{{ $tt('切割后格式') }}</span>
                            </label>
                            <single-select v-model="activeFormData.split_cue_target_format" :options="cueFormatList"
                                :placeholder="$tt('请选择')" @change="updateSys('split_cue_target_format')">
                            </single-select>
                        </div>

                        <div class="form-control">
                            <label class="label cursor-pointer justify-start gap-4">
                                <span class="label-text font-medium">{{ $tt('切割完成后删除整轨源文件') }}</span>
                                <input type="checkbox" class="toggle toggle-primary"
                                    v-model="activeFormData.split_cue_delete_source"
                                    @change="updateSys('split_cue_delete_source')" />
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 刮削周期配置抽屉 -->
        <div class="drawer drawer-end">
            <input id="scrape-schedule-config" type="checkbox" class="drawer-toggle" v-model="isOpenScrapeSchedule" />
            <div class="drawer-content">
            </div>
            <div class="drawer-side" style="z-index: 1000;">
                <label for="scrape-schedule-config" aria-label="close sidebar" class="drawer-overlay"
                    @click.stop="isOpenScrapeSchedule = false"></label>
                <div class="bg-base-100 text-base-content min-h-full w-80 md:w-96" v-if="isOpenScrapeSchedule"
                    @click.stop>
                    <div class="sticky top-0 bg-base-100 z-10 p-4 border-b border-base-200">
                        <div class="flex items-center justify-between">
                            <h3 class="text-lg font-semibold flex items-center">
                                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                </svg>{{ $tt('刮削周期配置') }}
                            </h3>
                            <button class="btn btn-circle btn-ghost btn-sm" @click="isOpenScrapeSchedule = false">
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M6 18L18 6M6 6l12 12"></path>
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div class="p-4 space-y-6">
                        <div class="bg-info/10 p-4 rounded-lg border border-info/20">
                            <div class="text-sm text-base-content/80">
                                <p class="mb-2">{{ $tt('配置后台刮削任务的执行周期') }}</p>
                                <p class="text-xs text-base-content/60">{{ $tt('Crontab 格式: 分钟 小时 日 月 星期') }}</p>
                            </div>
                        </div>

                        <!-- 当前配置 -->
                        <div v-if="scrapeSchedule.last_run_at" class="bg-base-200/50 p-4 rounded-lg">
                            <div class="text-sm">
                                <div class="flex items-center mb-2">
                                    <svg class="w-4 h-4 mr-2 text-success" fill="none" stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M5 13l4 4L19 7"></path>
                                    </svg>
                                    <span class="font-medium">{{ $tt('当前配置:') }}</span>
                                </div>
                                <div class="ml-6 space-y-1">
                                    <div>Crontab: <span class="font-mono text-primary">{{ scrapeSchedule.crontab
                                            }}</span></div>
                                    <div>{{ $tt('上次运行:') }} <span class="text-base-content/70">{{
                                            scrapeSchedule.last_run_at }}</span></div>
                                </div>
                            </div>
                        </div>

                        <!-- 快捷预设 -->
                        <div class="form-control">
                            <label class="label">
                                <span class="label-text font-medium">{{ $tt('快捷预设') }}</span>
                            </label>
                            <div class="space-y-2">
                                <label v-for="preset in cronPresets" :key="preset.value"
                                    class="label cursor-pointer justify-start gap-3 p-3 rounded-lg border border-base-200 hover:bg-base-50">
                                    <input type="radio" name="radio-cron-preset" class="radio radio-primary"
                                        :value="preset.value" v-model="selectedPreset" @change="applyPreset" />
                                    <div class="flex-1">
                                        <div class="font-medium">{{ preset.label }}</div>
                                        <div class="text-xs text-base-content/70">{{ preset.description }}</div>
                                    </div>
                                </label>
                            </div>
                        </div>

                        <!-- 自定义输入 -->
                        <div class="form-control">
                            <label class="label">
                                <span class="label-text font-medium">{{ $tt('自定义 Crontab 表达式') }}</span>
                            </label>
                            <input type="text" :placeholder="$tt('例如: */5 * * * * (每5分钟)')"
                                class="input input-bordered focus:input-accent w-full" v-model="customCrontab"
                                @input="selectedPreset = 'custom'" />
                            <label class="label">
                                <span class="label-text-alt text-base-content/60">{{ $tt('格式: 分钟(0-59) 小时(0-23) 日(1-31) 月(1-12) 星期(0-6)') }}</span>
                            </label>
                        </div>

                        <!-- 常用示例 -->
                        <div class="bg-base-200/50 p-4 rounded-lg">
                            <div class="font-medium mb-2">{{ $tt('常用示例:') }}</div>
                            <div class="text-xs space-y-1 text-base-content/70">
                                <div>{{ $tt('• */1 * * * * - 每分钟') }}</div>
                                <div>{{ $tt('• */3 * * * * - 每3分钟') }}</div>
                                <div>{{ $tt('• */5 * * * * - 每5分钟') }}</div>
                                <div>{{ $tt('• 0 * * * * - 每小时') }}</div>
                                <div>{{ $tt('• 0 */2 * * * - 每2小时') }}</div>
                                <div>{{ $tt('• 0 0 * * * - 每天凌晨') }}</div>
                            </div>
                        </div>

                        <!-- 保存按钮 -->
                        <div class="flex gap-2">
                            <button class="btn btn-primary flex-1" @click="saveScrapeSchedule">
                                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M5 13l4 4L19 7"></path>
                                </svg>{{ $tt('保存配置') }}</button>
                            <button class="btn btn-ghost" @click="isOpenScrapeSchedule = false">{{ $tt('取消') }}</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 配置帮助抽屉 -->
        <div class="drawer drawer-end">
            <input id="background-config-help" type="checkbox" class="drawer-toggle" v-model="isOpenConfigHelp" />
            <div class="drawer-content">
            </div>
            <div class="drawer-side z-50">
                <label for="background-config-help" aria-label="close sidebar" class="drawer-overlay"
                    @click="isOpenConfigHelp = false"></label>
                <div class="bg-base-100 text-base-content min-h-full w-full sm:w-[42rem] shadow-2xl"
                    v-if="isOpenConfigHelp" @click.stop>
                    <div class="sticky top-0 bg-base-100 z-10 p-6 border-b border-base-200">
                        <div class="flex items-center justify-between gap-4">
                            <h3 class="text-xl font-semibold flex items-center">
                                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z">
                                    </path>
                                </svg>{{ $tt('配置帮助') }}
                            </h3>
                            <button class="btn btn-circle btn-ghost btn-sm" @click="isOpenConfigHelp = false">
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M6 18L18 6M6 6l12 12"></path>
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div class="p-6 space-y-4 overflow-y-auto">
                        <div class="bg-primary/5 rounded-lg p-4 border border-primary/20">
                            <div class="font-bold text-primary mb-2">/app/download:</div>
                            <div class="text-sm text-base-content/80 mb-2">
                                {{ $tt('部署Docker应用时，需额外映射一个自定义目录到此路径，用于后台刮削监控的目录。') }}
                            </div>
                            <div class="bg-base-200 p-2 rounded text-sm break-all">
                                {{ $tt('例如：') }}<span class="text-accent font-medium">{{ $tt('&lt;宿主机上新建的自定义目录&gt;')
                                    }}</span>:/app/download
                            </div>
                        </div>

                        <div class="bg-secondary/5 rounded-lg p-4 border border-secondary/20">
                            <div class="font-bold text-secondary mb-2">/app/media/download:</div>
                            <div class="text-sm text-base-content/80 mb-2">
                                {{ $tt('项目部署时已映射') }}<span class="text-accent font-medium">{{ $tt('&lt;音乐媒体库目录&gt;')
                                    }}</span>:/app/media,
                                {{ $tt('只需在') }}<span class="text-accent font-medium">{{ $tt('&lt;音乐媒体库目录&gt;')
                                    }}</span>{{ $tt('路径下创建一个名为download的目录。') }}
                            </div>
                            <div class="bg-base-200 p-2 rounded text-sm break-all">
                                {{ $tt('例如：') }}<span class="text-accent font-medium">{{ $tt('&lt;音乐媒体库目录&gt;')
                                    }}</span>/download
                            </div>
                        </div>

                        <div class="bg-warning/10 p-4 rounded-lg border border-warning/20">
                            <div class="font-bold text-warning mb-2">{{ $tt('重要提示：') }}</div>
                            <ul class="list-disc list-inside text-sm text-base-content/80 space-y-1">
                                <li>{{ $tt('/app/download 需要重新部署在部署命令里添加') }}</li>
                                <li>{{ $tt('/app/media/download 无需重新部署') }}</li>
                                <li>{{ $tt('宿主目录不支持中文') }}</li>
                                <li>{{ $tt('/app/download目录和/app/media目录不能重合！') }}</li>
                                <li>{{ $tt('容器内地址/app/download不可修改，需保持一致！') }}</li>
                                <li>{{ $tt('一般用做新下载的音乐文件目录，不要把媒体库都放在这个目录下') }}</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import MusicMetaData from '@/views/home/musicMetaData'
import SingleSelect from "@/components/iview/SingleSelect";
import MultiSelect from "@/components/iview/MultiSelect";
import SvgIcon from '@/components/SvgIcon.vue'

function createDefaultConfig(index = 1, tt = (key, params = {}) =>
    String(key).replace(/\{(\w+)\}/g, (_, name) => params[name] ?? `{${name}}`)) {
    return {
        id: `config_${Date.now()}_${index}`,
        name: tt('配置 {index}', { index }),
        enabled: true,
        watch_subdir: `/app/download/config${index}`,
        silent_select_mode: 'hard',
        silence_modify_list: ['album_img', 'lyrics'],
        silence_source_list: ['netease', 'qmusic'],
        silence_is_skip_tag: false,
        silence_is_folder_album: false,
        tidy_music: 'move',
        silence_music_info: {},
        manual_mod: false,
        auto_mod: false,
        root_path: '/app/media',
        dir_list: [{ value: 'artist' }, { value: 'album' }],
        is_voiceprint: false,
        is_fix_encoding: false,
        is_tran_sim: false,
        tran_convert_mode: 'trad2sim',
        is_copy_file: false,
        copy_target_path: '/app/media/',
        copy_mode: 'copy',
        is_split_cue: false,
        split_cue_delete_source: false,
        split_cue_target_format: '',
        is_clean_music: false,
        clean_format_priority: ['flac', 'mp3'],
        clean_size_rule: 'smaller',
        clean_bitrate_rule: 'none',
        clean_sample_rule: 'none',
        clean_name_rule: 'none',
        clean_delete_rule: 'delete',
    }
}

function getBuiltInTagSources(tt) {
    return [
        { id: 'netease', name: tt('网易云音乐') },
        { id: 'migu', name: tt('咪咕音乐') },
        { id: 'qmusic', name: tt('QQ音乐') },
        { id: 'kugou', name: tt('酷狗音乐') },
        { id: 'kuwo', name: tt('酷我音乐') },
        { id: 'itunes', name: 'iTunes' },
        { id: 'spotify', name: 'Spotify' },
        { id: 'musicbrainz', name: 'MusicBrainz' },
    ]
}

function mergeTagSources(builtInSources, pluginSources) {
    const sourceMap = new Map()

    builtInSources.forEach((item) => {
        sourceMap.set(item.id, item)
    })

    pluginSources.forEach((item) => {
        if (!item || !item.id) {
            return
        }
        sourceMap.set(item.id, item)
    })

    return Array.from(sourceMap.values())
}

export default {
    name: 'BackgroundScraping',
    components: {
        SingleSelect,
        MusicMetaData,
        MultiSelect,
        SvgIcon
    },
    props: {
        formData: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            preDataCollapse: true,
            autoModCollapse: true,
            tidyCollapse: true,
            isOpenAutoMod: false,
            isOpenManualMod: false,
            isOpenTidyMusic: false,
            isOpenCopyFile: false,
            isOpenTranConfig: false,
            isOpenSplitCueConfig: false,
            isOpenScrapeSchedule: false,
            isOpenConfigHelp: false,
            reloadImg: true,
            celeryTaskLastTime: '',
            backgroundScrapeConfigs: [createDefaultConfig(1)],
            activeConfigId: '',
            editingConfigId: '',
            editingConfigName: '',
            editingOriginalName: '',
            watchRoot: '/app/download',
            availableWatchDirs: [],
            scrapeSchedule: {
                crontab: '',
                last_run_at: ''
            },
            selectedPreset: '',
            customCrontab: '',
            tranConvertModeOptions: [
                { id: 'trad2sim', name: this.$tt('繁体转简体'), description: this.$tt('将繁体中文转换为简体中文') },
                { id: 'sim2trad', name: this.$tt('简体转繁体'), description: this.$tt('将简体中文转换为繁体中文') }
            ],
            cronPresets: [
                { label: this.$tt('每分钟'), value: '*/1 * * * *', description: this.$tt('每1分钟执行一次') },
                { label: this.$tt('每3分钟'), value: '*/3 * * * *', description: this.$tt('每3分钟执行一次（默认）') },
                { label: this.$tt('每10分钟'), value: '*/10 * * * *', description: this.$tt('每10分钟执行一次') },
                { label: this.$tt('每小时'), value: '0 * * * *', description: this.$tt('每小时整点执行') },
                { label: this.$tt('每2小时'), value: '0 */2 * * *', description: this.$tt('每2小时执行一次') },
                { label: this.$tt('每天凌晨'), value: '0 0 * * *', description: this.$tt('每天凌晨0点执行') },
                { label: this.$tt('自定义'), value: 'custom', description: this.$tt('使用自定义 crontab 表达式') }
            ],
            modifyListBatch: [
                { id: 'title', name: this.$tt('标题') },
                { id: 'artist', name: this.$tt('艺术家') },
                { id: 'album', name: this.$tt('专辑') },
                { id: 'albumartist', name: this.$tt('专辑艺术家') },
                { id: 'album_img', name: this.$tt('专辑封面') },
                { id: 'year', name: this.$tt('年份') },
                { id: 'lyrics', name: this.$tt('歌词') },
                { id: 'is_save_lyrics_file', name: this.$tt('歌词(LRC文件)') },
                { id: 'discnumber', name: this.$tt('光盘编号') },
                { id: 'tracknumber', name: this.$tt('音轨号') },
                { id: 'composer', name: this.$tt('作曲家') },
                { id: 'lyricist', name: this.$tt('作词家') },
                { id: 'language', name: this.$tt('语言') }
            ],
            tidyList: [
                { id: 'title', name: this.$tt('标题') },
                { id: 'artist', name: this.$tt('艺术家') },
                { id: 'album', name: this.$tt('专辑') },
                { id: 'album_artist', name: this.$tt('专辑艺术家') },
                { id: 'album_type', name: this.$tt('专辑类型') },
                { id: 'genre', name: this.$tt('风格') },
                { id: 'language', name: this.$tt('语言') },
                { id: 'track_number', name: this.$tt('音轨号') },
                { id: 'disc_number', name: this.$tt('光盘编号') },
                { id: 'comment', name: this.$tt('描述') },
                { id: 'custom', name: this.$tt('自定义') }
            ],
            resourceListBatch: [],
            sizeRuleList: [
                { 'id': 'larger', 'name': this.$tt('删除较大文件') },
                { 'id': 'smaller', 'name': this.$tt('删除较小文件') },
                { 'id': 'none', 'name': this.$tt('不判断') },
            ],
            bitRuleList: [
                { 'id': 'larger', 'name': this.$tt('删除较大比特率') },
                { 'id': 'smaller', 'name': this.$tt('删除较小比特率') },
                { 'id': 'none', 'name': this.$tt('不判断') },
            ],
            sampleRuleList: [
                { 'id': 'larger', 'name': this.$tt('删除较大采样率') },
                { 'id': 'smaller', 'name': this.$tt('删除较小采样率') },
                { 'id': 'none', 'name': this.$tt('不判断') },
            ],
            nameRuleList: [
                { 'id': 'larger', 'name': this.$tt('删除含有后缀名') },
                { 'id': 'smaller', 'name': this.$tt('删除不含有后缀名') },
                { 'id': 'none', 'name': this.$tt('不判断') },
            ],
            formatList: [
                { id: 'flac', name: 'FLAC' },
                { id: 'mp3', name: 'MP3' },
                { id: 'ape', name: 'APE' },
                { id: 'wav', name: 'WAV' },
                { id: 'aiff', name: 'AIFF' },
                { id: 'aif', name: 'AIF' },
                { id: 'tta', name: 'TTA' },
                { id: 'm4a', name: 'M4A' },
                { id: 'ogg', name: 'OGG' },
                { id: 'opus', name: 'OPUS' },
                { id: 'wma', name: 'WMA' },
                { id: 'dsf', name: 'DSF' },
                { id: 'dff', name: 'DFF' },
                { id: 'wmv', name: 'WMV' },
                { id: 'dts', name: 'DTS' },
                { id: 'aac', name: 'AAC' }
            ],
            cueFormatList: [
                { id: '', name: this.$tt('维持原格式') },
                { id: 'mp3', name: 'MP3' },
                { id: 'wav', name: 'WAV' },
                { id: 'flac', name: 'FLAC' },
                { id: 'wma', name: 'WMA' },
                { id: 'ogg', name: 'OGG' }
            ],
            deleteRuleList: [
                { 'id': 'delete', 'name': this.$tt('删除') },
                { 'id': 'move', 'name': this.$tt('移动') },
            ]
        }
    },
    computed: {
        activeFormData() {
            const matched = this.backgroundScrapeConfigs.find(item => item.id === this.activeConfigId)
            if (matched) {
                return matched
            }
            return this.backgroundScrapeConfigs[0] || createDefaultConfig(1, this.$tt)
        }
    },
    async mounted() {
        await this.loadBackgroundScrapeConfigs()
        this.loadTagSources()
        this.fetchCeleryTaskLastTime()
    },
    watch: {
        activeConfigId() {
            this.filterActiveSourceList()
        },
    },
    methods: {
        startRenameConfig(config) {
            if (!config || !config.id) {
                return
            }
            this.editingConfigId = config.id
            this.editingConfigName = config.name || ''
            this.editingOriginalName = config.name || ''

            this.$nextTick(() => {
                const refName = `renameInput-${config.id}`
                const ref = this.$refs[refName]
                const inputEl = Array.isArray(ref) ? ref[0] : ref
                if (inputEl && typeof inputEl.focus === 'function') {
                    inputEl.focus()
                    if (typeof inputEl.select === 'function') {
                        inputEl.select()
                    }
                }
            })
        },
        cancelRenameConfig() {
            this.editingConfigId = ''
            this.editingConfigName = ''
            this.editingOriginalName = ''
        },
        handleRenameKeydown(event) {
            if (event.key === 'Enter') {
                event.preventDefault()
                this.finishRenameConfig()
                return
            }
            if (event.key === 'Escape') {
                event.preventDefault()
                const current = this.backgroundScrapeConfigs.find(item => item.id === this.editingConfigId)
                if (current) {
                    current.name = this.editingOriginalName
                }
                this.cancelRenameConfig()
            }
        },
        async finishRenameConfig() {
            const configId = this.editingConfigId
            if (!configId) {
                return
            }

            const target = this.backgroundScrapeConfigs.find(item => item.id === configId)
            if (!target) {
                this.cancelRenameConfig()
                return
            }

            const finalName = String(this.editingConfigName || '').trim() || this.$tt('未命名配置')
            const oldName = target.name || ''
            target.name = finalName

            this.cancelRenameConfig()

            if (finalName === oldName) {
                return
            }

            const ok = await this.saveBackgroundConfigs(false)
            if (!ok) {
                target.name = oldName
            }
        },
        normalizeWatchSubdir(subdir) {
            return String(subdir || '')
                .replace(/\\/g, '/')
                .trim()
                .replace(/^\/+|\/+$/g, '')
                .replace(/\/+/g, '/')
        },
        handleWatchSubdirChange(value) {
            if (!this.activeFormData) {
                return
            }
            this.activeFormData.watch_subdir = String(value || '').trim()
            this.saveBackgroundConfigs(true)
        },
        validateEnabledConfigConflict() {
            const enabledConfigs = this.backgroundScrapeConfigs
                .filter(item => item && item.enabled !== false)
                .map(item => ({
                    ...item,
                    watch_subdir: this.normalizeWatchSubdir(item.watch_subdir),
                }))

            for (let i = 0; i < enabledConfigs.length; i += 1) {
                const left = enabledConfigs[i]
                const leftSubdir = left.watch_subdir
                for (let j = i + 1; j < enabledConfigs.length; j += 1) {
                    const right = enabledConfigs[j]
                    const rightSubdir = right.watch_subdir

                    const isLeftParent =
                        (leftSubdir === '' && rightSubdir !== '') ||
                        (leftSubdir !== '' && rightSubdir.startsWith(`${leftSubdir}/`))
                    const isRightParent =
                        (rightSubdir === '' && leftSubdir !== '') ||
                        (rightSubdir !== '' && leftSubdir.startsWith(`${rightSubdir}/`))

                    if (!isLeftParent && !isRightParent) {
                        continue
                    }

                    const leftPath = leftSubdir ? `/app/download/${leftSubdir}` : '/app/download'
                    const rightPath = rightSubdir ? `/app/download/${rightSubdir}` : '/app/download'
                    this.$myMsg.notify({
                        title: this.$tt('启用配置存在父子目录冲突: {left} 与 {right}', {
                            left: `${left.name || left.id}(${leftPath})`,
                            right: `${right.name || right.id}(${rightPath})`,
                        }),
                        type: 'warning',
                        time: 2500,
                    })
                    return false
                }
            }
            return true
        },
        validateConfigBusinessRules() {
            const invalidConfig = this.backgroundScrapeConfigs.find((item) => (
                item
                && item.is_split_cue
                && (!item.is_copy_file || item.copy_mode !== 'copy')
            ))
            if (!invalidConfig) {
                return true
            }
            this.$myMsg.notify({
                title: this.$tt('配置 {name} 启用了 CUE 切割，必须同时启用复制文件且复制模式为复制文件本体', {
                    name: invalidConfig.name || invalidConfig.id,
                }),
                type: 'warning',
                time: 2500,
            })
            return false
        },
        buildLegacyConfig() {
            const config = createDefaultConfig(1, this.$tt)
            const keys = [
                'enabled',
                'silent_select_mode', 'silence_modify_list', 'silence_source_list',
                'silence_is_skip_tag', 'silence_is_folder_album', 'tidy_music',
                'silence_music_info', 'manual_mod', 'auto_mod', 'root_path', 'dir_list',
                'is_voiceprint', 'is_fix_encoding', 'is_tran_sim', 'tran_convert_mode',
                'is_copy_file', 'copy_target_path', 'copy_mode', 'is_split_cue',
                'split_cue_delete_source', 'split_cue_target_format', 'is_clean_music', 'clean_format_priority',
                'clean_size_rule', 'clean_bitrate_rule', 'clean_sample_rule', 'clean_name_rule', 'clean_delete_rule'
            ]
            keys.forEach((key) => {
                if (this.formData[key] !== undefined) {
                    config[key] = this.formData[key]
                }
            })
            config.id = 'default'
            config.name = this.$tt('默认配置')
            config.watch_subdir = ''
            return config
        },
        async loadBackgroundScrapeConfigs() {
            try {
                const res = await this.$api.Task.getBackgroundScrapeConfigs()
                if (res.result && res.data) {
                    this.watchRoot = res.data.watch_root || '/app/download'
                    this.availableWatchDirs = Array.isArray(res.data.available_dirs) ? res.data.available_dirs : []
                    this.backgroundScrapeConfigs = Array.isArray(res.data.configs) && res.data.configs.length > 0
                        ? res.data.configs
                        : [this.buildLegacyConfig()]
                    this.backgroundScrapeConfigs = this.backgroundScrapeConfigs.map(item => ({
                        enabled: item.enabled !== false,
                        ...item,
                    }))
                    this.activeConfigId = res.data.active_config_id || this.backgroundScrapeConfigs[0].id
                    return
                }
            } catch (e) {
                // ignore
            }
            this.backgroundScrapeConfigs = [this.buildLegacyConfig()]
            this.activeConfigId = this.backgroundScrapeConfigs[0].id
        },
        async saveBackgroundConfigs(notify = true) {
            if (!this.validateEnabledConfigConflict()) {
                return false
            }
            if (!this.validateConfigBusinessRules()) {
                return false
            }
            try {
                const payload = {
                    configs: this.backgroundScrapeConfigs,
                    active_config_id: this.activeConfigId || (this.activeFormData && this.activeFormData.id) || '',
                }
                const res = await this.$api.Task.saveBackgroundScrapeConfigs(payload)
                if (res.result && res.data) {
                    this.watchRoot = res.data.watch_root || this.watchRoot
                    this.availableWatchDirs = Array.isArray(res.data.available_dirs) ? res.data.available_dirs : []
                    this.backgroundScrapeConfigs = Array.isArray(res.data.configs) ? res.data.configs : this.backgroundScrapeConfigs
                    this.activeConfigId = res.data.active_config_id || this.activeConfigId
                    if (notify) {
                        this.$myMsg.notify({
                            title: this.$tt('system.update.successTitle'),
                            type: 'success',
                            time: 1000,
                        })
                    }
                    return true
                }
                this.$myMsg.notify({
                    title: res.msg || this.$tt('更新失败'),
                    type: 'error',
                    time: 2000,
                })
                return false
            } catch (e) {
                this.$myMsg.notify({
                    title: this.$tt('更新失败'),
                    type: 'error',
                    time: 2000,
                })
                return false
            }
        },
        addConfigTab() {
            const config = createDefaultConfig(this.backgroundScrapeConfigs.length + 1, this.$tt)
            this.backgroundScrapeConfigs.push(config)
            this.activeConfigId = config.id
            this.saveBackgroundConfigs(false).then((ok) => {
                if (ok) {
                    this.startRenameConfig(config)
                }
            }).catch(() => { })
        },
        removeActiveConfigTab(configId = '') {
            if (this.backgroundScrapeConfigs.length <= 1) {
                this.$myMsg.notify({
                    title: this.$tt('至少保留一套配置'),
                    type: 'warning',
                    time: 1500,
                })
                return
            }
            const targetId = configId || this.activeConfigId
            const currentIndex = this.backgroundScrapeConfigs.findIndex(item => item.id === targetId)
            if (currentIndex < 0) {
                return
            }
            this.backgroundScrapeConfigs.splice(currentIndex, 1)
            const nextIndex = Math.max(0, currentIndex - 1)
            this.activeConfigId = this.backgroundScrapeConfigs[nextIndex].id
            this.cancelRenameConfig()
            this.saveBackgroundConfigs(false).catch(() => { })
        },
        changeActiveConfig(id) {
            if (this.editingConfigId && this.editingConfigId !== id) {
                this.finishRenameConfig()
            }
            this.activeConfigId = id
            this.saveBackgroundConfigs(false).catch(() => { })
        },
        filterActiveSourceList() {
            if (!this.activeFormData || !Array.isArray(this.activeFormData.silence_source_list)) {
                return
            }
            const availableSourceIds = this.resourceListBatch.map(item => item.id)
            const filteredSourceList = this.activeFormData.silence_source_list.filter(sourceId => availableSourceIds.includes(sourceId))

            if (filteredSourceList.length > 0) {
                this.activeFormData.silence_source_list = filteredSourceList
                return
            }

            if (this.activeFormData.silence_source_list.length === 0 && this.resourceListBatch.length > 0) {
                this.activeFormData.silence_source_list = [this.resourceListBatch[0].id]
            }
        },
        // 加载标签源列表
        loadTagSources() {
            const builtInSources = getBuiltInTagSources(this.$tt)
            this.$api.scriptMgmt.getTagSources({ 'page_size': 99, 'status': 'active' }).then((res) => {
                if (res.result && res.data && res.data.items) {
                    // 将插件数据转换为前端需要的格式
                    const sources = res.data.items.map(plugin => ({
                        id: plugin.unique_id,
                        name: plugin.display_name
                    }));
                    // 过滤掉 smart_tag
                    const excludeIds = ['smart_tag', 'acoustid', 'musicbrainz']
                    const pluginSources = sources.filter(plugin => !excludeIds.includes(plugin.id));
                    this.resourceListBatch = mergeTagSources(builtInSources, pluginSources)
                } else {
                    // 如果获取失败，使用默认的标签源列表
                    this.resourceListBatch = builtInSources
                }
                this.filterActiveSourceList()
            }).catch(() => {
                // 请求出错时使用默认列表
                this.resourceListBatch = builtInSources
                this.filterActiveSourceList()
            });
        },
        updateSys(key) {
            if (key === 'is_watch_file') {
                this.$emit('update-sys', key)
                return
            }
            this.saveBackgroundConfigs(true).catch(() => { })
        },
        redirectEditDetail(path) {
            this.$emit('redirect-edit', path)
        },
        handleBackendExe() {
            this.$api.Task.debugTask2({ 'mode': 'backend_exe' }).then((res) => {
                if (res.result) {
                    this.$myMsg.notify({
                        title: this.$tt('执行成功！'),
                        type: 'success',
                        time: 2000,
                    })
                }
            })
        },
        async handleClearBackgroundState() {
            if (!this.activeFormData || !this.activeFormData.id) {
                return
            }
            const res = await this.$api.Task.clearBackgroundScrapeState({ config_id: this.activeFormData.id })
            if (res.result) {
                this.$myMsg.notify({
                    title: this.$tt('清空成功'),
                    type: 'success',
                    time: 1500,
                })
            }
        },
        async handleMarkBackgroundDone() {
            if (!this.activeFormData || !this.activeFormData.id) {
                return
            }
            const res = await this.$api.Task.markBackgroundScrapeDone({ config_id: this.activeFormData.id })
            if (res.result) {
                this.$myMsg.notify({
                    title: this.$tt('标记成功'),
                    type: 'success',
                    time: 1500,
                })
            }
        },
        fetchCeleryTaskLastTime() {
            this.$api.Task.celeryTaskLastTime().then((res) => {
                if (res.result) {
                    this.celeryTaskLastTime = res.data.last_run_at
                }
            })
        },
        handleSaveManualMusic() {
            this.isOpenManualMod = false
            this.updateSys('silence_music_info')
        },
        addInput() {
            if (!Array.isArray(this.activeFormData.dir_list)) {
                this.activeFormData.dir_list = []
            }
            this.activeFormData.dir_list.push({ value: '' })
        },
        removeInput(index) {
            this.activeFormData.dir_list.splice(index, 1)
            this.updateSys('dir_list')
        },
        toggleCollapse(collapse) {
            this[collapse] = !this[collapse]
        },
        openAutoModConfig() {
            this.isOpenAutoMod = true
        },
        openManualModConfig() {
            this.isOpenManualMod = true
        },
        openTidyMusicConfig() {
            this.isOpenTidyMusic = true
        },
        openCopyFileConfig() {
            this.isOpenCopyFile = true
        },
        openTranConfig() {
            this.isOpenTranConfig = true
        },
        openSplitCueConfig() {
            this.isOpenSplitCueConfig = true
        },
        openScrapeScheduleConfig() {
            // 打开抽屉时获取当前配置
            this.fetchScrapeSchedule()
            this.isOpenScrapeSchedule = true
        },
        openConfigHelpDrawer() {
            this.isOpenConfigHelp = true
        },
        fetchScrapeSchedule() {
            // 获取当前刮削周期配置
            this.$api.Task.getScrapeSchedule().then((res) => {
                if (res.result) {
                    this.scrapeSchedule = res.data
                    this.customCrontab = res.data.crontab

                    // 查找匹配的预设
                    const matchPreset = this.cronPresets.find(p => p.value === res.data.crontab)
                    if (matchPreset) {
                        this.selectedPreset = matchPreset.value
                    } else {
                        this.selectedPreset = 'custom'
                    }
                }
            })
        },
        applyPreset() {
            // 应用预设值
            if (this.selectedPreset !== 'custom') {
                this.customCrontab = this.selectedPreset
            }
        },
        saveScrapeSchedule() {
            // 保存刮削周期配置
            const crontab = this.selectedPreset === 'custom' ? this.customCrontab : this.selectedPreset

            if (!crontab || crontab === 'custom') {
                this.$myMsg.notify({
                    title: this.$tt('请输入有效的 crontab 表达式'),
                    type: 'warning',
                    time: 2000,
                })
                return
            }

            // 简单验证 crontab 格式
            const parts = crontab.trim().split(/\s+/)
            if (parts.length !== 5) {
                this.$myMsg.notify({
                    title: this.$tt('crontab 表达式格式错误，应为5个字段'),
                    type: 'error',
                    time: 2000,
                })
                return
            }

            this.$api.Task.updateScrapeSchedule({ crontab: crontab }).then((res) => {
                if (res.result) {
                    this.$myMsg.notify({
                        title: this.$tt('刮削周期配置已更新！'),
                        type: 'success',
                        time: 2000,
                    })
                    this.isOpenScrapeSchedule = false
                    this.fetchScrapeSchedule()
                } else {
                    this.$myMsg.notify({
                        title: res.msg || this.$tt('更新失败'),
                        type: 'error',
                        time: 2000,
                    })
                }
            })
        }
    }
}
</script>
